import { z } from "zod";

/** utils */
const NonEmptyStr = z.string().trim().min(1, "Wajib diisi");
const DateYMD = z.string();

/** 1) Base object (masih ZodObject, jadi bisa omit/pick) */
export const incomingLetterBase = z
  .object({
    letterTypeId: z.coerce.number().int().positive().or(z.string()),
    letterCharacteristicId: z.coerce.number().int().positive().or(z.string()),
    letterPriorityId: z.coerce.number().int().positive().or(z.string()),
    incommingDate: DateYMD,
    letterDate: DateYMD,
    instansi: NonEmptyStr,
    possition: NonEmptyStr,
    sender: NonEmptyStr,
    letterNumber: NonEmptyStr,
    target: z
      .array(z.coerce.number().int().positive().or(z.string()))
      .min(1, "Minimal 1 target"),
    concern: NonEmptyStr,
    summary: NonEmptyStr,
    letterFile: z.any(),
    isDisposition: z.string(),
    disposition: z
      .array(
        z.object({
          userIds: z
            .array(z.coerce.number().int().positive().or(z.string()))
            .min(1, "Minimal 1 user"),
          dateLimit: DateYMD,
          intruction: NonEmptyStr,
          aditionalNote: z.string().trim().optional().default(""),
        })
      )
      .default([]),
    copy: z.array(NonEmptyStr).min(1, "Minimal 1 tembusan").default([]),
  })
  .strict();

export type IncomingLetterPayload = z.infer<typeof incomingLetterBase>;

// ============

export const generateDynamicFormIncomingLetterPhysicalSchema = (
  fields?: LetterSection[]
) => {
  const shape: Record<string, z.ZodTypeAny> = {};
  const slugs: string[] = [];

  if (fields)
    for (const field of fields) {
      const slug = field.keyWord;
      slugs.push(slug);

      let base: z.ZodTypeAny;

      switch (field.contentType) {
        case "LONG_TEXT":
          base = z.string();
          break;
        case "SHORT_TEXT":
          base = z.string();
          break;
        case "TIME":
          base = z.string();
          break;
        case "FILE":
          base = z.preprocess(
            (val) => {
              // Jika val berupa string non-kosong → anggap sudah ada file (misal URL)
              if (typeof val === "string" && val.trim() !== "") {
                return [val]; // dummy array biar zod lolos validasi
              }
              if (!val) return [];
              if (val instanceof File) return [val];
              return val;
            },
            z
              .array(z.any())
              .refine((arr) => arr.length > 0, "Wajib upload satu file")
          );
          break;

        case "DATE":
          base = z.string().refine((val) => !isNaN(Date.parse(val)), {
            message: `${field.keyWord.split("_").join(" ").toLowerCase()} harus berupa tanggal valid`,
          });
          break;

        default:
          base = z.any();
      }

      if (field.isRequired) {
        if (base instanceof z.ZodString || base instanceof z.ZodArray) {
          shape[slug] = base.min(
            1,
            `${field.keyWord.split("_").join(" ").toLowerCase()} wajib diisi`
          );
        } else {
          shape[slug] = base; // untuk tipe lain, tidak pakai .min()
        }
      } else {
        shape[slug] = base.optional();
      }
    }

  const schema = z.object(shape);
  return { schema, slugs };
};

// ========

export const forwardDispoValidation = z.object({
  disposition: z
    .array(
      z.object({
        userIds: z
          .array(z.coerce.number().int().positive().or(z.string()))
          .min(1, "Minimal 1 user"),
        dateLimit: DateYMD,
        intruction: NonEmptyStr,
        aditionalNote: z.string().trim().optional().default(""),
      })
    )
    .default([]),
});

export type ForwardDispositionPayload = z.infer<typeof forwardDispoValidation>;
